import { useApolloClient } from "@apollo/client";
import { Avatar, Group, Text } from "@mantine/core";
import { GetClientPage } from "src/api/graphql/queries/Client";
import {
  GetClientPageQuery,
  GetClientPageQueryVariables,
  MemberStatus,
} from "src/generated/graphql";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useCallback, useContext, useEffect } from "react";
import { AppContext } from "src/api/AppContext";
import { BiUserCircle } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import {
  ModalId,
  ROUTES,
  ROUTE_NAMES,
  DEFAULT_ORDER,
  EMPLOYEE_ROLE_OBJ,
} from "src/utils/constants";
import { Button } from "@mantine/core";
import { useModals } from "@mantine/modals";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import ClientProfile from "../ClientDetail/profile";
import GuestLeadProfile from "../GuestLeadProfile";
import { useToggle } from "@mantine/hooks";
import useWritePermission from "src/hooks/usePermission";

export type ClientData = ArrayElement<
  GetClientPageQuery["clientPage"]["items"]
>;

type IClientList = {
  memberStatus: MemberStatus;
};

const NavRoute = {
  [MemberStatus.Member]: ROUTES.CLIENT,
  [MemberStatus.Guest]: ROUTES.GUEST,
  [MemberStatus.Lead]: ROUTES.LEAD,
};

const ClientList = ({ memberStatus }: IClientList) => {
  const modals = useModals();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.clients);
  const { setCurrentPageTitle } = useContext(AppContext);
  const { query } = useApolloClient();
  const navigate = useNavigate();
  const [refetchObserver, refetchTableData] = useToggle();

  const getClients = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<ClientData>> => {
    const defaultFilter = {
      field: "memberStatus",
      type: "select",
      values: [memberStatus],
    };
    let { data } = await query<GetClientPageQuery, GetClientPageQueryVariables>(
      {
        query: GetClientPage,
        variables: {
          pagination: {
            count: fetchParams.count,
            page: fetchParams.page,
            orderBy: fetchParams.orderBy || DEFAULT_ORDER.name,
            filters: fetchParams.filters
              ? [...fetchParams.filters, defaultFilter]
              : [defaultFilter],
          },
        },
      }
    );

    if (data.clientPage) {
      return {
        rowCount: data.clientPage.totalRows,
        rowData: data.clientPage.items as ClientData[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.CLIENT);

    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleModal = useCallback(() => {
    memberStatus === MemberStatus.Member
      ? handleModalClient()
      : handleModalGuestLead();
  }, [memberStatus]);

  const handleModalClient = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Member",
      children: <ClientProfile refetchData={refetchTableData} />,
    });
  }, []);

  const handleModalGuestLead = useCallback(() => {
    const heading = memberStatus === MemberStatus.Guest ? "Guest" : "Lead";
    modals.openModal({
      modalId: ModalId.CreateGuest,
      closeOnClickOutside: false,
      title: `Create ${heading}`,
      children: (
        <GuestLeadProfile
          refetchData={refetchTableData}
          memberStatus={memberStatus}
        />
      ),
    });
  }, [memberStatus]);

  const handleNavigation = (id: string): void => {
    navigate(`${NavRoute[memberStatus]}/${id}`);
  };

  const ShowTable = useCallback(() => {
    return (
      <ScrollableDataGrid<ClientData>
        topMargin={122}
        columnConfigs={[
          {
            field: "profilePicUrl",
            name: "",
            sortable: false,
            cellRenderer: (rowItem) => {
              return rowItem.user.profilePicUrl ? (
                <Avatar
                  radius="xl"
                  sx={{
                    ":root": {
                      justifyContent: "center",
                      alignItems: "center",
                      display: "flex",
                    },
                  }}
                  size="sm"
                  src={rowItem.user.profilePicUrl}
                />
              ) : (
                <div style={{ padding: 0, borderRadius: 18 }}>
                  <BiUserCircle size={26} />
                </div>
              );
            },
          },
          {
            field: "name",
            name: "Name",
            parentRelationField: "user",
            filter: {
              field: "name",
              type: "text",
              parentRelationField: "user",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                style={{ cursor: "pointer" }}
                onClick={() => handleNavigation(rowItem.id)}
              >
                {`${rowItem.user.firstName} ${rowItem.user.lastName}`}
              </Text>
            ),
          },
          {
            field: "email",
            name: "Email",
            parentRelationField: "user",
            filter: {
              field: "email",
              type: "text",
              parentRelationField: "user",
            },
            sortable: true,
            valueParser: (rowItem) => rowItem.user.email,
          },
          {
            field: "client_createdDate",
            name: "Created",
            filter: {
              field: "createdDate",
              type: "dateRange",
            },
            sortable: true,
            valueParser: (rowItem) => {
              return new Date(rowItem.user.createdDate).toDateString();
            },
          },
        ]}
        dataFetch={getClients}
      />
    );
  }, [refetchObserver]);

  return (
    <>
      <Group>
        {isAllowWrite && (
          <Button mb={"md"} onClick={handleModal}>
            Create +
          </Button>
        )}
      </Group>
      <ShowTable />
    </>
  );
};

export default ClientList;
